<template>
  <div class="order-summary">
    <NftSummary 
      v-if="isOrderSummaryReady"
      :orderData="orderData"
      :nftData="nftData"
      :buttonMessage="buttonMessage"
      @summaryAction="handleSummaryActionClicked"
    />
    <SkeleLoader
      v-else
      :lines="20"
      :hasContentImg="true"
      contentIsFullWidth
    />
  </div>
</template>
<script setup>
// ====================================
// Packages
// ====================================
import { ref, reactive, computed, onMounted } from "vue";
import { useTitle } from "@vueuse/core";
import { useRoute, useRouter } from "vue-router";
// ====================================
// Storage
// ====================================
import { useGlobalStore } from "@/stores/globalStore";
import { useOrderFormStore } from "@/stores/forms/orderFormStore";
// ====================================
// Composables
// ====================================
import useApiHelpers from "@/composables/api/helpers";
// ====================================
// Components
// ====================================
import NftSummary from "@/components/layout/nft/NftSummary.vue";
import SkeleLoader from "@/components/ui/loaders/SkeleLoader.vue";

const orderStore = useOrderFormStore();
const router = useRouter();
const route = useRoute();
const { getRequest } = useApiHelpers();

const orderData = reactive({
  orderId: "",
  fiatType: "",
  fiatAmount: "",
  feeAmount: "",
  paymentMethod: "",
  status: "",
})
const nftData = reactive({
  name: "",
  image: "",
  video: "",
})
const buttonReturnUrl = reactive({
  successUrl: "",
  cancelUrl: "",
  failureUrl: "",
})
const buttonMessage = ref("")
const isOrderSummaryReady = ref(false);
const title = computed(() => {
  return "Banxa - Fiat to NFT";
}); 

const orderCancelledStatus = ["EXPIRED", "CANCELED"]
const orderFailedStatus = ["DECLINED", "FRAUD", "REFUNDED"]

const updateOrderData = (orderSummary) => {
  orderData.feeAmount = orderSummary.surchargeValue;
  orderData.fiatAmount = orderSummary.amountPaidOriginal;
  orderData.fiatType = orderSummary.fiatType;
  orderData.paymentMethod = orderSummary.paymentMethod;
  orderData.orderId = orderSummary.orderRef;
  orderData.status = orderSummary.status;
}

const updateNftData = (nftSummaryData) => {
  nftData.name = nftSummaryData.name;
  nftData.image = nftSummaryData.image;
  nftData.video = nftSummaryData.video
  if (nftSummaryData.media?.type === "image"){
    nftData.image = nftSummaryData.media.link;
  }
  if (nftSummaryData.media?.type === "video"){
    nftData.video = nftSummaryData.media.link;
  }
}

const updateButtonMsgAndUrl = (partnerReturnUrlData) => {
  buttonReturnUrl.successUrl = partnerReturnUrlData.return_url_on_success;
  buttonReturnUrl.cancelUrl = partnerReturnUrlData.return_url_on_cancelled;
  buttonReturnUrl.failureUrl = partnerReturnUrlData.return_url_on_failure;
  buttonMessage.value = partnerReturnUrlData.message || "";
}

const handleSummaryActionClicked = () => {
  if (buttonReturnUrl.cancelUrl !== "" && orderCancelledStatus.includes(orderData.status.toUpperCase())){
    window.location = buttonReturnUrl.cancelUrl;
    return;
  }

  if (buttonReturnUrl.failureUrl !== "" && orderFailedStatus.includes(orderData.status.toUpperCase())){
    window.location = buttonReturnUrl.failureUrl;
    return;
  }

  if (buttonReturnUrl.successUrl !== "" ) {
    window.location = buttonReturnUrl.successUrl;
    return;
  }

  router.push("/");
}

onMounted(async () => {
  orderStore.setActiveMode("NFT-BUY");
  const response = await getRequest(`/status/${route.params.orderId}`);
  const { summary, partnerReturnUrl } = response.data.data;
  updateOrderData(summary);
  updateNftData(summary.nftData);
  if (partnerReturnUrl?.data?.message && partnerReturnUrl?.data?.message !== "") {
    updateButtonMsgAndUrl(partnerReturnUrl.data);
  }
  isOrderSummaryReady.value = true;
})

useTitle(title);
</script>
<style lang="scss">
.order-summary {
  width: 100%;
  max-width: 50rem;
}
</style>
